/**
 * StickyCTABar — Bottom-fixed conversion bar for mobile/desktop
 * Appears after scrolling ~600px on main marketing pages.
 * Dismissible. Hosts: Request a quote (/start), Book 15-min Call, WhatsApp.
 * Sits left-aligned on desktop and stops short on mobile so the
 * floating chat / WhatsApp widget (bottom-right) stays visible.
 */
import { waHref } from '@/lib/whatsappPrefill';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { startHref } from '@/lib/ctaSource';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Calendar, X, MessageCircle } from 'lucide-react';
import { usePageStickyCtaActive } from '@/lib/pageStickyCta';
import { trackPrimaryCTAClick, trackBookingClick, trackWhatsAppClick } from '@/lib/analytics';

const CTA_TEXT: Record<string, { value: string; estimate: string; book: string; wa: string; close: string }> = {
  en: { value: 'Multilingual contact center & IT teams — guided onboarding.', estimate: 'Request a quote', book: 'Book a 15-min call', wa: 'WhatsApp us', close: 'Dismiss' },
  fr: { value: 'Centre de contact multilingue & équipes IT — mise en place accompagnée.', estimate: 'Demander un devis', book: 'Réserver un appel 15 min', wa: 'Nous écrire sur WhatsApp', close: 'Fermer' },
  es: { value: 'Contact center multilingüe y equipos IT — implementación acompañada.', estimate: 'Solicitar presupuesto', book: 'Reservar llamada 15 min', wa: 'WhatsApp', close: 'Cerrar' },
  de: { value: 'Mehrsprachiges Contact Center & IT-Teams — begleitete Einführung.', estimate: 'Angebot anfordern', book: '15-min Call buchen', wa: 'WhatsApp', close: 'Schließen' },
  it: { value: 'Contact center multilingue & team IT — avvio accompagnato.', estimate: 'Richiedi preventivo', book: 'Prenota call 15 min', wa: 'WhatsApp', close: 'Chiudi' },
  pt: { value: 'Contact center multilíngue & equipas IT — implementação acompanhada.', estimate: 'Solicitar orçamento', book: 'Agendar call 15 min', wa: 'WhatsApp', close: 'Fechar' },
  nl: { value: 'Meertalig contactcenter & IT-teams — begeleide onboarding.', estimate: 'Offerte aanvragen', book: 'Plan een 15-min call', wa: 'WhatsApp', close: 'Sluiten' },
  ar: { value: 'مركز اتصال متعدد اللغات وفرق تقنية — إعداد بمرافقة فريقنا.', estimate: 'اطلب عرض سعر', book: 'احجز مكالمة 15 دقيقة', wa: 'واتساب', close: 'إغلاق' },
};

const HIDDEN_ON_SEGMENTS = ['careers', 'about', 'contact', 'admin', 'portal', 'jobs', 'legal', 'whistleblowing', 'auth', 'login', 'signup'];
const STORAGE_KEY = 'sticky_cta_dismissed_v2';
const WA_NUMBER = '212658380381';

export function StickyCTABar() {
  const { language } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const pageBarActive = usePageStickyCtaActive();
  const shouldShow = !pageBarActive && !pathSegments.some(seg => HIDDEN_ON_SEGMENTS.includes(seg));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === '1');
  }, []);

  useEffect(() => {
    if (!shouldShow || dismissed) return;
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [shouldShow, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
  };

  const t = CTA_TEXT[language] || CTA_TEXT.en;
  const isRtl = language === 'ar';
  const Arrow = isRtl ? ArrowLeft : ArrowRight;
  if (!shouldShow || dismissed) return null;

  const waUrl = waHref(WA_NUMBER, language);
  const quoteHref = startHref(language, location.pathname, 'sticky-cta-bar');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          /* leave right padding on mobile so floating chat/WhatsApp (bottom-right) stays clickable */
          className="fixed bottom-0 left-0 right-0 md:right-24 z-40 bg-gradient-to-r from-primary to-primary/90 shadow-[0_-4px_20px_rgba(0,0,0,0.25)] pr-20 md:pr-0"
          role="region"
          dir={isRtl ? 'rtl' : undefined}
          aria-label={t.estimate}
        >
          {/* Mobile */}
          <div className="md:hidden px-3 py-2.5 flex items-center gap-2">
            <Link
              to={quoteHref} onClick={() => trackPrimaryCTAClick("quote_request", "sticky_cta_bar")}
              className="flex-1 min-h-11 flex items-center justify-center gap-1.5 bg-white text-primary px-3 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              {t.estimate}
              <Arrow className="w-3.5 h-3.5" aria-hidden />
            </Link>
            <a
              href={`/${language}/booking`} onClick={() => trackBookingClick("sticky_cta_bar")}
              className="p-2.5 bg-white/15 rounded-full hover:bg-white/25"
              aria-label={t.book}
            >
              <Calendar className="w-4 h-4 text-white" />
            </a>
            <a
              href={waUrl} onClick={() => trackWhatsAppClick()}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 bg-[#25D366] rounded-full hover:bg-[#20BA5A]"
              aria-label={t.wa}
            >
              <MessageCircle className="w-4 h-4 text-white" />
            </a>
            <button
              type="button"
              onClick={handleDismiss}
              className="p-2 text-white/70 hover:text-white"
              aria-label={t.close}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-4 px-6 py-2.5">
            <p className="text-white/95 text-sm font-medium flex-1">{t.value}</p>
            <Link
              to={quoteHref} onClick={() => trackPrimaryCTAClick("quote_request", "sticky_cta_bar")}
              className="flex items-center gap-2 bg-white text-primary px-5 py-2 rounded-full font-semibold text-sm hover:bg-white/90 transition hover:scale-[1.03]"
            >
              {t.estimate}
              <Arrow className="w-4 h-4" aria-hidden />
            </Link>
            <a
              href={`/${language}/booking`} onClick={() => trackBookingClick("sticky_cta_bar")}
              className="flex items-center gap-2 border border-white/40 text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-white/10"
            >
              <Calendar className="w-4 h-4" />
              {t.book}
            </a>
            <a
              href={waUrl} onClick={() => trackWhatsAppClick()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white px-3 py-2 rounded-full font-medium text-sm hover:bg-[#20BA5A]"
              aria-label={t.wa}
            >
              <MessageCircle className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-white/70 hover:text-white p-1"
              aria-label={t.close}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
